import {useAuth} from "react-oidc-context";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormLabel,
    Input,
    InputLabel,
    Modal,
    Radio,
    RadioGroup,
    Typography
} from "@mui/material";
import {Add} from "@mui/icons-material";
import {Company} from "../entities/company";
import {TokenRequests} from "../components/TokenRequests";
import {Tokens} from "../components/Tokens";
import {TokenRequest} from "../entities/token-request";
import Box from "@mui/material/Box";
import {CompanyCard} from "../components/CompanyCard";
import {CompanyUpdateDTO} from "../entities/companyUpdateDTO";
import {enqueueSnackbar} from "notistack";

export const CompanyPage = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const [company, setCompany] = useState<Company>()
    const {companyId} = useParams();
    const [tokens, setTokens] = useState([]);
    const [availableTokens, setAvailableTokens] = useState(0)
    const [totalTokens, setTotalTokens] = useState(0)
    const [tokenRequests, setTokenRequests] = useState<TokenRequest[]>([]);
    const [newTokenRequestOpen, setNewTokenRequestOpen] = useState(false);
    const [requestedQuantity, setRequestedQuantity] = useState(0)
    const [proofOfPayment, setProofOfPayment] = useState("");
    const [isPaidToken, setIsPaidToken] = useState(false);
    useEffect(() => {
        if (auth.isAuthenticated) {
            const token = auth.user?.access_token;
            fetch(`/api/admin/companies/${companyId}`, {headers: {"Authorization": `Bearer ${token}`}})
                .then(res => res.json())
                .then(data => {
                    setCompany(data)
                })
                .then(() => fetch(`/api/admin/companies/${companyId}/tokens?status=AVAILABLE`, {headers: {"Authorization": `Bearer ${token}`}}))
                .then(res => res.json())
                .then(data => setTokens(data))
                .then(() => fetch(`/api/admin/companies/${companyId}/token_requests`, {headers: {"Authorization": `Bearer ${token}`}}))
                .then(res => res.json())
                .then(data => setTokenRequests(data))
                .then(() => fetch(`/api/admin/companies/${companyId}/token_stats`, {headers: {"Authorization": `Bearer ${token}`}}))
                .then(res => res.json())
                .then(data => {
                    setAvailableTokens(data.availableTokens)
                    setTotalTokens(data.totalTokens)
                })
                .catch(error => console.log(error));

        }
    }, [auth.isAuthenticated, auth.user?.access_token, companyId]);

    const loadAllTokens = () => {
        const token = auth.user?.access_token;
        fetch(`/api/admin/companies/${companyId}/tokens`, {headers: {"Authorization": `Bearer ${token}`}})
            .then(res => res.json())
            .then(data => setTokens(data))

    }


    const handleTokenRequestClick = (tokenRequestId: string) => {
        navigate(`/companies/${companyId}/token_requests/${tokenRequestId}`)

    }

    const handleModalClose = () => {

    }

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    const submitNewTokenRequest = () => {
        const token = auth.user?.access_token;
        fetch(`/api/admin/companies/${companyId}/token_requests`, {
            method: 'POST',
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({quantity: requestedQuantity, isPaidToken})
        })
            .then(res => res.json())
            .then(data => {
                setTokenRequests([...tokenRequests, data])
                setNewTokenRequestOpen(false)
            })
            .catch(error => console.log(error));
    }

    const companyUpdate = (company: CompanyUpdateDTO) => {
        const token = auth.user?.access_token;
        fetch(`/api/admin/companies/${companyId}`, {
            body: JSON.stringify(company),
            method: 'PATCH', headers: {"Authorization": `Bearer ${token}`, "Content-Type": "application/json"}
        })
            .then(res => res.json())
            .then(data => {
                setCompany(data)
            }).catch(err => {
            console.error(err)
            enqueueSnackbar("An error has occurred while modifying the company", {variant: 'error'})
        })
    }
    return company ? <>
        <CompanyCard company={company} onBackClick={() => navigate("/companies")} onEdit={companyUpdate}
                     showAdminDetails={true}/>
        <Card>
            <CardHeader title="Token requests"/>
            <CardActions>
                <Button onClick={() => setNewTokenRequestOpen(true)} variant="outlined"
                        disabled={company.membershipCanceled}><Add></Add>New
                    request</Button>
            </CardActions>
            <CardContent>
                <TokenRequests tokenRequests={tokenRequests} onRowClick={handleTokenRequestClick}
                               showAdminInfos={true}/>
            </CardContent>
        </Card>
        <Card>
            <CardHeader title="Tokens"
                        action={<Button onClick={() => loadAllTokens()} variant="outlined">See all tokens</Button>}/>
            <CardContent>
                <Typography>
                    Tokens: {availableTokens} / {totalTokens}
                </Typography>
                <Tokens tokens={tokens} showAdminInfos={true}/>
            </CardContent>
        </Card>
        <Modal open={newTokenRequestOpen} onClose={() => handleModalClose()}>
            <Box sx={{...modalStyle, width: 400}}>
                <Typography variant="h6">Request tokens</Typography>
                <form>
                    <FormControl>
                        <InputLabel htmlFor="quantity">Quantity</InputLabel>
                        <Input id="quantity" type="number" placeholder="quantity" value={requestedQuantity}
                               onChange={(ev) => setRequestedQuantity(Number.parseInt(ev.target.value))}/>
                    </FormControl>
                    <FormControl>
                        <InputLabel htmlFor="proof">Proof of payment (wire ID, link to PDF etc.)</InputLabel>
                        <Input id="proof" type="text" placeholder="Proof of payment" value={proofOfPayment}
                               onChange={(ev) => setProofOfPayment(ev.target.value)}/>
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="isPaid">Is for paid tokens ?</FormLabel>
                        <RadioGroup name="isPaid" defaultValue={isPaidToken}
                                    onChange={ev => setIsPaidToken(ev.target.value as unknown as boolean)}>
                            <FormControlLabel control={<Radio/>} label={"Yes"} value={true}/>
                            <FormControlLabel control={<Radio/>} label={"No"} value={false}/>
                        </RadioGroup>
                    </FormControl>
                </form>
                <Button color="success" variant="contained" onClick={() => submitNewTokenRequest()}>Submit</Button>
                <Button color="error" onClick={() => setNewTokenRequestOpen(false)}>Cancel</Button>
            </Box>
        </Modal>

    </> : <CircularProgress/>
}